import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

/**
 * «Ahorros» (Vadik, 26-oct-2026): la ventana de la brújula del Plan con las
 * travesías. Aquí, la lógica pura: qué categorías parecen un ahorro sin
 * destino (para que Gavi proponga ponérselo) y lo que se dijo que no.
 */

/** Palabras de ahorro en castellano y catalán (sin tildes, en minúscula). */
const PALABRAS_AHORRO = [
  'ahorro',
  'ahorrar',
  'hucha',
  'colchon',
  'imprevisto',
  'emergencia',
  'fondo',
  'vacaciones',
  'viaje',
  'estalvi',
  'guardiola',
  'matalas',
  'imprevist',
  'vacances',
  'viatge',
];

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/** ¿El nombre suena a dinero que se aparta (ahorro, vacaciones…)? */
export function pareceAhorro(nombre: string): boolean {
  const palabras = normalizar(nombre).split(/[^\p{L}]+/u);
  return palabras.some(p => PALABRAS_AHORRO.some(a => p.startsWith(a)));
}

/**
 * Categorías de gasto visibles que suenan a ahorro pero no son travesía ni
 * pago fijo, y a las que no se dijo «No». En el orden del Plan.
 */
export function ahorrosSinDestino({
  grupos,
  travesias,
  fijos,
  descartadas,
}: {
  grupos: readonly CategoryGroupEntity[];
  travesias: ReadonlySet<string>;
  fijos: ReadonlySet<string>;
  descartadas: ReadonlySet<string>;
}): CategoryEntity[] {
  return grupos
    .filter(g => !g.is_income && !g.hidden)
    .flatMap(g => g.categories ?? [])
    .filter(
      c =>
        !c.hidden &&
        !c.is_income &&
        !travesias.has(c.id) &&
        !fijos.has(c.id) &&
        !descartadas.has(c.id) &&
        pareceAhorro(c.name),
    );
}

/** Pref `ahorros-descartadas`: JSON array de ids (lo roto cuenta como vacío). */
export function leerDescartadas(raw: string | undefined): Set<string> {
  if (!raw) {
    return new Set();
  }
  try {
    const valor: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(valor)
        ? valor.filter((v): v is string => typeof v === 'string')
        : [],
    );
  } catch {
    return new Set();
  }
}

export function conDescartada(raw: string | undefined, id: string): string {
  return JSON.stringify([...leerDescartadas(raw), id].filter(unico));
}

function unico<T>(v: T, i: number, lista: readonly T[]): boolean {
  return lista.indexOf(v) === i;
}
